import React, { useState, useEffect } from "react";
import EventsTab from "./tabs/EventsTab";
import ClubsTab from "./tabs/ClubsTab";
import SportsTab from "./tabs/SportsTab";
import PlayersTab from "./tabs/PlayersTab";
import LeaguesTab from "./tabs/LeaguesTab";
import LeagueTablesTab from "./tabs/LeagueTablesTab";
import CoachesTab from "./tabs/CoachesTab";
import TitlesTab from "./tabs/TitlesTab";
import {
  fetchSports,
  fetchClubs,
  fetchMatches,
  addMatch,
} from "../../services/matchService";
import { fetchPlayers, updatePlayer } from "../../services/playerService";
import { PageHeader } from "../admin";

function Sports() {
  const [activeTab, setActiveTab] = useState("events");
  const [matches, setMatches] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [sports, setSports] = useState([]);
  const [players, setPlayers] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showClubModal, setShowClubModal] = useState(false);
  const [showSportModal, setShowSportModal] = useState(false);
  const [showPlayerModal, setShowPlayerModal] = useState(false);

  // Fetch initial data
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true);
        const [sportsData, clubsData, matchesData, playersData, leaguesData, coachesResponse] =
          await Promise.all([
            fetchSports(),
            fetchClubs(),
            fetchMatches(),
            fetchPlayers(),
            fetch(`${process.env.REACT_APP_API_URL}/leagues`).then(res => res.json()),
            fetch(`${process.env.REACT_APP_API_URL}/coaches`).then(res => res.json()),
          ]);
        
        // Extract coaches data from the response structure
        const coachesData = coachesResponse.data || [];
        
        setSports(sportsData || []);
        setClubs(clubsData || []);
        setMatches(matchesData || []);
        setPlayers(playersData || []);
        setLeagues(leaguesData || []);
        setCoaches(coachesData);
      } catch (err) {
        console.error('Error in loadInitialData:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, []);

  const handleAddMatch = async (newMatch) => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/matches`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          sportId: parseInt(newMatch.sportId),
          date: new Date(newMatch.date).toISOString(),
          location: newMatch.location,
          homeTeamId: parseInt(newMatch.homeTeamId),
          awayTeamId: parseInt(newMatch.awayTeamId),
          scores: newMatch.scores,
        }),
      });

      if (!response.ok) throw new Error("Failed to add match");
      const addedMatch = await response.json();

      // Enrich the match with related data
      const enrichedMatch = {
        ...addedMatch,
        Sport: sports.find(
          (sport) => sport.sportId === parseInt(addedMatch.sportId)
        ),
        HomeTeam: clubs.find(
          (club) => club.clubId === parseInt(addedMatch.homeTeamId)
        ),
        AwayTeam: clubs.find(
          (club) => club.clubId === parseInt(addedMatch.awayTeamId)
        ),
      };

      setMatches((prevMatches) => [...prevMatches, enrichedMatch]);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (match) => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/matches/${match.matchesId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(match),
        }
      );

      if (!response.ok) throw new Error("Failed to update match");

      // Fetch fresh data after successful update
      const [matchesData] = await Promise.all([fetchMatches()]);
      setMatches(matchesData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (match) => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/matches/${match.matchesId}`,
        {
          method: "DELETE",
        }
      );

      if (!response.ok) throw new Error("Failed to delete match");
      setMatches(matches.filter((m) => m.matchesId !== match.matchesId));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClub = (club) => {
    try {
      setLoading(true);
      fetch(`${process.env.REACT_APP_API_URL}/clubs/${club.clubId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(club),
      })
        .then((response) => {
          if (!response.ok) throw new Error("Failed to update club");
          return response.json();
        })
        .then((updatedClub) => {
          setClubs(
            clubs.map((c) => (c.clubId === club.clubId ? updatedClub : c))
          );
        })
        .catch((err) => setError(err.message))
        .finally(() => setLoading(false));
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleDeleteClub = (club) => {
    try {
      setLoading(true);
      fetch(`${process.env.REACT_APP_API_URL}/clubs/${club.clubId}`, {
        method: "DELETE",
      })
        .then((response) => {
          if (!response.ok) throw new Error("Failed to delete club");
          setClubs(clubs.filter((c) => c.clubId !== club.clubId));
        })
        .catch((err) => setError(err.message))
        .finally(() => setLoading(false));
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleEditSport = (sport) => {
    try {
      setLoading(true);
      fetch(`${process.env.REACT_APP_API_URL}/sports/${sport.sportId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(sport),
      })
        .then((response) => {
          if (!response.ok) throw new Error("Failed to update sport");
          return response.json();
        })
        .then((updatedSport) => {
          setSports(
            sports.map((s) => (s.sportId === sport.sportId ? updatedSport : s))
          );
        })
        .catch((err) => setError(err.message))
        .finally(() => setLoading(false));
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleDeleteSport = (sport) => {
    try {
      setLoading(true);
      fetch(`${process.env.REACT_APP_API_URL}/sports/${sport.sportId}`, {
        method: "DELETE",
      })
        .then((response) => {
          if (!response.ok) throw new Error("Failed to delete sport");
          setSports(sports.filter((s) => s.sportId !== sport.sportId));
        })
        .catch((err) => setError(err.message))
        .finally(() => setLoading(false));
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleEditPlayer = async (player) => {
    try {
      setLoading(true);
      const updatedPlayer = await updatePlayer(player.playerId, player);
      setPlayers(
        players.map((p) => (p.playerId === player.playerId ? updatedPlayer : p))
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePlayer = (player) => {
    try {
      setLoading(true);
      fetch(`${process.env.REACT_APP_API_URL}/players/${player.playerId}`, {
        method: "DELETE",
      })
        .then((response) => {
          if (!response.ok) throw new Error("Failed to delete player");
          setPlayers(players.filter((p) => p.playerId !== player.playerId));
        })
        .catch((err) => setError(err.message))
        .finally(() => setLoading(false));
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleAddPlayer = async (player) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/players`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(player),
      });
      if (!response.ok) throw new Error("Failed to add player");
      const newPlayer = await response.json();
      // Update the players list with the new player
      setPlayers([...players, newPlayer]);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEditLeague = async (league) => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/leagues/${league.leagueId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(league),
        }
      );
      if (!response.ok) throw new Error("Failed to update league");
      const updatedLeague = await response.json();
      setLeagues(leagues.map((l) => (l.leagueId === league.leagueId ? updatedLeague : l)));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLeague = async (league) => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/leagues/${league.leagueId}`,
        {
          method: "DELETE",
        }
      );
      if (!response.ok) throw new Error("Failed to delete league");
      setLeagues(leagues.filter((l) => l.leagueId !== league.leagueId));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const renderTabContent = () => {
    console.log('Current coaches state:', coaches); // Debug log
    
    switch (activeTab) {
      case "events":
        return (
          <EventsTab
            matches={matches}
            sports={sports}
            homeTeams={clubs}
            awayTeams={clubs}
            handleAdd={handleAddMatch}
            handleEdit={handleEdit}
            handleDelete={handleDelete}
          />
        );
      case "clubs":
        return (
          <ClubsTab
            clubs={clubs}
            loading={loading}
            error={error}
            showClubModal={showClubModal}
            handleEditClub={handleEditClub}
            handleDeleteClub={handleDeleteClub}
          />
        );
      case "sports":
        return (
          <SportsTab
            sports={sports}
            loading={loading}
            error={error}
            showSportModal={showSportModal}
            handleEditSport={handleEditSport}
            handleDeleteSport={handleDeleteSport}
          />
        );
      case "players":
        return (
          <PlayersTab
            players={players}
            clubs={clubs}
            loading={loading}
            error={error}
            setError={setError}
            setPlayers={setPlayers}
            handleEditPlayer={handleEditPlayer}
            handleDeletePlayer={handleDeletePlayer}
          />
        );
      case "league-tables":
        return <LeagueTablesTab />;
      case "leagues":
        return (
          <LeaguesTab
            leagues={leagues}
            sports={sports}
            loading={loading}
            error={error}
            handleEditLeague={handleEditLeague}
            handleDeleteLeague={handleDeleteLeague}
          />
        );
      case "coaches":
        return <CoachesTab />;
      case "titles":
        return (
          <TitlesTab
            clubs={clubs}
            players={players}
            coaches={coaches}
          />
        );
      default:
        return null;
    }
  };

  const TAB_COUNTS = {
    events: matches.length,
    clubs: clubs.length,
    sports: sports.length,
    players: players.length,
    leagues: leagues.length,
    coaches: coaches.length,
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Sports"
        description="Events, clubs, players, leagues, coaches and titles"
      />

      <div className="flex gap-1 flex-wrap border-b border-line">
        {[
          { id: "events", label: "Events" },
          { id: "clubs", label: "Clubs" },
          { id: "sports", label: "Sports" },
          { id: "players", label: "Players" },
          { id: "leagues", label: "Leagues" },
          { id: "league-tables", label: "League Tables" },
          { id: "coaches", label: "Coaches" },
          { id: "titles", label: "Titles" },
        ].map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === tab.id
                ? "border-accent text-white"
                : "border-transparent text-gray-400 hover:text-white"
            }`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
            {TAB_COUNTS[tab.id] !== undefined && (
              <span
                className={`text-[11px] font-semibold px-1.5 py-0.5 rounded ${
                  activeTab === tab.id
                    ? "bg-accent-dim text-accent-soft"
                    : "bg-surface-700 text-gray-400"
                }`}
              >
                {TAB_COUNTS[tab.id]}
              </span>
            )}
          </button>
        ))}
      </div>

      {renderTabContent()}
    </div>
  );
}

export default Sports;
